import { Error as ErrorIcon } from '@mui/icons-material';
import { Grid, useMediaQuery } from '@mui/material';
import { calculateDuration, dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalChip, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { memo, useState } from 'react';
import styled from 'styled-components';

import More3Points from '../../../../../../assets/svg/More3Points';
import CommentsIcon from '../../../../../../assets/svg/Plan/report/CommentsIcon';
import CustomFieldsIcon from '../../../../../../assets/svg/Plan/report/CustomFieldsIcon';
import NobodyAtHomeIcon from '../../../../../../assets/svg/Plan/report/NobodyAtHomeIcon';
import OtherCancelReasons from '../../../../../../assets/svg/Plan/report/OtherCancelReasons';
import PhotosIcon from '../../../../../../assets/svg/Plan/report/PhotosIcon';
import SignatureIcon from '../../../../../../assets/svg/Plan/report/SignatureIcon';
import WrongAddressIcon from '../../../../../../assets/svg/Plan/report/WrongAddressIcon';
import { numericToText } from '../../../../../../components/form-fields/FormTimeWindows';
import { getStopStatusColor } from '../../../../../../constants/Colors';
import { Driver } from '../../../../../../domain/driver/Driver';
import { Stop } from '../../../../../../domain/stop/Stop';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveUserCredentials } from '../../../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../../../react-components/Text';
import StopCircleColor from '../../../../../shared/StopCircleColor';
import { stopTimewindowsAreInvalid } from '../../../components/BrokenConstraintsTooltip';
import ProblemsTooltip from '../../../components/ProblemsTooltip';
import { useRetrieveActivePlan } from '../../../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../hooks/plan-details/useRetrieveActivePlanStops';
import { PlanStopsActionsMenu } from '../../../menus/PlanStopsActionsMenu';

const StopChipContainer = styled(Grid)`
  border: 1px solid ${RoutalPalette.neutral10};
  border-radius: 5px;
  background-color: ${RoutalPalette.neutral02n};
  height: 52px;
  align-content: center !important;
`;

const StopChipHeader = styled(Grid)`
  height: 40px;
`;
const StopHeaderRow = styled(Grid)`
  height: 20px;
  padding-left: 8px;
  padding-right: 8px;
`;

const StopChipCounterBadge = styled(Grid)`
  position: absolute;
  right: -8px;
  top: -8px;
  background-color: ${RoutalPalette.primary40};
  color: ${RoutalPalette.neutral00};
  border-radius: 50%;
  height: 22px;
  width: 22px;
  text-align: center;
  font-size: 12px;
`;

interface PlanOverviewStopChipProps {
  stop: Stop;
  driver?: Driver;
  selectedStops?: string[];
  showDraggingBadge?: boolean;
  isDragging?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, stop: Stop) => void;
}

export const PlanOverviewStopChip = memo(
  ({
    stop,
    driver,
    selectedStops = [],
    showDraggingBadge = false,
    isDragging = false,
    onClick,
  }: PlanOverviewStopChipProps) => {
    const { data: drivers } = useRetrieveActivePlanDrivers();
    const { data: stops } = useRetrieveActivePlanStops();
    const { data: plan } = useRetrieveActivePlan();
    const { data: activeProject } = useRetrieveActiveProject();
    const { data: userCredentials } = useRetrieveUserCredentials();
    const { setRightPanelInfo } = useRetrieveRightPanelInfo();
    const isLargeDesktop = useMediaQuery(`(min-width: 1920px)`);

    const [isHover, setIsHover] = useState(false);

    const isSelected = selectedStops.includes(stop.id);

    const [contextAnchorStopChip, setContextAnchorStopChip] = useState<
      | {
          screenPosition: {
            top: number;
            left: number;
          };
        }
      | undefined
    >(undefined);

    const onDoubleClickStop = () => {
      setRightPanelInfo({
        detailType: `stop`,
        detailId: `${stop.id}`,
        detailTab: null,
      });
    };

    const renderCompleted = () => {
      const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);

      const backgroundColor = RoutalPalette.neutral00;

      const iconColor = getStopStatusColor(stop.status);

      const tooltipErrorBackground = getStopStatusColor(`canceled`);

      return (
        <Grid container direction="row">
          {planReport?.cancel_reason && (
            <RoutalTooltip
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
              title={intl.formatMessage({ id: `properties.stop.report.reason.${planReport.cancel_reason}` })}
              placement="right"
              backgroundColor={tooltipErrorBackground}
              arrowColor={tooltipErrorBackground}
            >
              <Grid
                style={{
                  height: `18px`,
                  padding: `4px 8px`,
                  display: `flex`,
                  alignItems: `center`,
                  position: `relative`,
                  borderRadius: `5px`,
                  marginRight: `4px`,
                  backgroundColor,
                }}
              >
                {(() => {
                  if (planReport.cancel_reason === `nobody`) {
                    return (
                      <NobodyAtHomeIcon
                        style={{
                          color: iconColor,
                          height: `14px`,
                          width: `14px`,
                        }}
                      />
                    );
                  }
                  if (planReport.cancel_reason === `wrong_address`) {
                    return (
                      <WrongAddressIcon
                        style={{
                          color: iconColor,
                          height: `14px`,
                          width: `14px`,
                        }}
                      />
                    );
                  }
                  return (
                    <OtherCancelReasons
                      style={{
                        color: iconColor,
                        height: `14px`,
                        width: `14px`,
                      }}
                    />
                  );
                })()}
              </Grid>
            </RoutalTooltip>
          )}

          {planReport?.comments && (
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor,
              }}
            >
              <CommentsIcon color={iconColor} secondaryColor={RoutalPalette.neutral00} />
            </Grid>
          )}

          {planReport?.signature && (
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor,
              }}
            >
              <SignatureIcon color={iconColor} />
            </Grid>
          )}

          {(planReport?.images ?? []).length > 0 && (
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor,
              }}
            >
              <PhotosIcon color={iconColor} />
            </Grid>
          )}

          {planReport?.custom_fields?.props && Object.keys(planReport.custom_fields.props).length !== 0 && (
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor,
              }}
            >
              <CustomFieldsIcon color={iconColor} />
            </Grid>
          )}
        </Grid>
      );
    };

    const renderPending = () => (
      <>
        <Grid
          item
          style={{
            position: `absolute`,
            left: `0px`,
            top: `0px`,
            width: `1rem`,
            height: `1rem`,
            textAlign: `left`,
            display: `flex`,
            alignItems: `center`,
            justifyContent: `center`,
          }}
        >
          <ProblemsTooltip type="stop" stop={stop} tooltipPlacement="right" planDrivers={drivers} driver={driver}>
            <ErrorIcon
              style={{
                color: RoutalPalette.warning.dark,
                fontSize: `16px`,
                backgroundColor: RoutalPalette.neutral00,
                borderRadius: `50%`,
              }}
            />
          </ProblemsTooltip>
        </Grid>
        {/* TODO: WEIGHT ERRORS */}
        {stop.pickup && (
          <RoutalChip
            label={
              <Text
                variant="ultra-small"
                style={{
                  fontWeight: 400,
                  padding: 0,
                  color: RoutalPalette.secondary20,
                }}
                intlId="with_pickup"
              />
            }
          />
        )}
        {stop.time_windows && stop.time_windows.length > 0 && activeProject?.constraints.time_windows && (
          <RoutalChip
            backgroundColor={
              driver?.status === `not_started` && stopTimewindowsAreInvalid(stop)
                ? RoutalPalette.error.dark
                : RoutalPalette.neutral02
            }
            label={
              <Text
                variant="ultra-small"
                style={{
                  fontWeight: 400,
                  padding: 0,
                  color:
                    driver?.status === `not_started` && stopTimewindowsAreInvalid(stop)
                      ? RoutalPalette.neutral00
                      : RoutalPalette.secondary20,
                }}
              >{`${numericToText(stop.time_windows[0])} ${stop.time_windows.length > 1 ? `+` : ``}`}</Text>
            }
          />
        )}
        {stop.duration !== undefined && (
          <RoutalChip
            label={
              <Text
                variant="ultra-small"
                style={{
                  fontWeight: 400,
                  padding: 0,
                  color: RoutalPalette.secondary20,
                }}
              >{`${calculateDuration(stop.duration)} min`}</Text>
            }
          />
        )}
        {/* TODO: WEIGHT ERRORS */}
        {stop.weight && activeProject?.constraints.weight ? (
          <RoutalChip
            label={
              <Text
                variant="ultra-small"
                style={{
                  fontWeight: 400,
                  padding: 0,
                  color: RoutalPalette.secondary20,
                }}
              >{`${Math.floor(stop.weight) !== stop.weight ? stop.weight.toFixed(2) : stop.weight} ${
                activeProject?.units?.weight
              }`}</Text>
            }
          />
        ) : null}
        {/* TODO: VOLUME ERRORS */}
        {stop.volume && activeProject?.constraints.volume ? (
          <RoutalChip
            label={
              <Text
                variant="ultra-small"
                style={{
                  fontWeight: 400,
                  padding: 0,
                  color: RoutalPalette.secondary20,
                }}
              >{`${Math.floor(stop.volume) !== stop.volume ? stop.volume.toFixed(2) : stop.volume} ${
                activeProject?.units?.volume
              }`}</Text>
            }
          />
        ) : null}
      </>
    );

    const renderTime = () => {
      const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);
      if (planReport && stop.status !== `pending`) {
        return (
          <Text
            variant="small"
            style={{
              color:
                stop.status === `completed`
                  ? RoutalPalette.markerStopColors.completed.medium
                  : stop.status === `canceled`
                  ? RoutalPalette.markerStopColors.canceled.medium
                  : RoutalPalette.markerStopColors.incomplete.medium,
              fontWeight: 500,
              whiteSpace: `nowrap`,
              display: `flex`,
              alignItems: `center`,
            }}
          >
            {String(new Date(planReport.created_at).getHours()).padStart(2, `0`)}:
            {String(new Date(planReport.created_at).getMinutes()).padStart(2, `0`)}
          </Text>
        );
      }

      if (stop.estimated_arrival_time && driver?.status !== `not_started`) {
        return (
          <Text
            variant="small"
            style={{
              color: RoutalPalette.secondary20,
              fontWeight: 600,
              whiteSpace: `nowrap`,
              display: `flex`,
              alignItems: `center`,
            }}
          >
            {dateFormatShortTime(stop.estimated_arrival_time)}
          </Text>
        );
      }

      if (stop.planned_arrival_time || stop.planned_arrival_time === 0) {
        const hm = parseSecondsToHM(stop.planned_arrival_time, true);

        return (
          <Text
            variant="small"
            style={{
              color: RoutalPalette.secondary00,
              fontWeight: 600,
              whiteSpace: `nowrap`,
              display: `flex`,
              alignItems: `center`,
            }}
          >
            {hm}
          </Text>
        );
      }

      return null;
    };

    const stopLabel = stop.label ?? ``;
    return (
      <StopChipContainer
        container
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        alignContent="space-between"
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClickStop();
        }}
        style={{
          backgroundColor: isSelected ? RoutalPalette.neutral05 : RoutalPalette.neutral02n,
          position: `relative`,
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextAnchorStopChip({
            screenPosition: {
              top: e.clientY,
              left: e.clientX,
            },
          });
        }}
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          onClick?.(e, stop);
        }}
      >
        {/* Dragging badge */}
        {showDraggingBadge && isDragging && selectedStops.length > 1 && (
          <StopChipCounterBadge item>
            <Grid container justifyContent="center" alignContent="center" style={{ height: `100%` }}>
              {selectedStops.length}
            </Grid>
          </StopChipCounterBadge>
        )}

        <StopChipHeader container justifyContent="center" flexDirection="column" wrap="nowrap">
          <StopHeaderRow container direction="row" alignItems="center" justifyContent={`space-between`}>
            <Grid container alignItems="center" width={`auto`}>
              <StopCircleColor
                driver={driver}
                status={stop.status}
                isPickup={stop.type === `pickup`}
                number={stop.order != null ? stop.order + 1 : undefined}
                planDrivers={drivers ?? []}
                isSelected={false}
                // isSelected={!!selectedStops.find((s) => s === stop.id)}
                size={20}
              />
              <HSpacer small />
              <RoutalTooltip mouseEnterDelay={0.5} mouseLeaveDelay={0} id={stop.id} title={stopLabel} placement="top">
                {stopLabel !== `` ? (
                  <Text
                    variant="small"
                    style={{
                      fontWeight: isLargeDesktop ? 400 : 300,
                      cursor: `pointer`,
                      textOverflow: `ellipsis`,
                      overflow: `hidden`,
                      whiteSpace: `nowrap`,
                    }}
                  >
                    {stopLabel.length > 35 ? `${stopLabel.substring(0, 35)}...` : stopLabel}
                  </Text>
                ) : (
                  <Text
                    variant="small"
                    intlId="no_name"
                    style={{
                      color: RoutalPalette.secondary00,
                      fontWeight: isLargeDesktop ? 400 : 300,
                      cursor: `pointer`,
                    }}
                  />
                )}
              </RoutalTooltip>
            </Grid>
            <Grid container width={`auto`}>
              {renderTime()}
            </Grid>
          </StopHeaderRow>
          <StopHeaderRow
            container
            direction="row"
            alignItems="center"
            wrap="nowrap"
            height="16px"
            marginTop="4px"
            style={{ overflow: `hidden` }}
          >
            {stop.status !== `pending` && renderCompleted()}
            {stop.status === `pending` && renderPending()}
          </StopHeaderRow>
        </StopChipHeader>

        {isHover && (
          <Grid
            container
            style={{
              position: `absolute`,
              width: `50px`,
              height: `100%`,
              right: 0,
              background: isSelected
                ? `linear-gradient(90deg, ${RoutalPalette.neutral05 + `00`} 0%, ${RoutalPalette.neutral05} 30%, ${
                    RoutalPalette.neutral05
                  } 100%)`
                : `linear-gradient(90deg, ${RoutalPalette.neutral02n + `00`} 0%, ${RoutalPalette.neutral02n} 30%, ${
                    RoutalPalette.neutral02n
                  } 100%)`,
              borderRadius: `0 4px 4px 0`,
            }}
            justifyContent="center"
            alignContent="center"
          >
            <PlanStopsActionsMenu
              type="popover"
              stops={[stop]}
              showOpenCustomers={userCredentials?.subscription.test(`customers`)}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
              }}
            />
          </Grid>
        )}
        {contextAnchorStopChip && (
          <PlanStopsActionsMenu
            type="contextual"
            setContextAnchor={setContextAnchorStopChip}
            showOpenCustomers={userCredentials?.subscription.test(`customers`)}
            contextAnchor={{
              screenPosition: contextAnchorStopChip.screenPosition,
            }}
            stops={(stops ?? []).filter((s: Stop) => selectedStops.includes(s.id) || s.id === stop.id)}
          />
        )}
      </StopChipContainer>
    );
  }
);
